import { z } from 'zod';
/**
 * ehr#375 - the sliding scale (Chase's SLIDING_SCALE_10-12-V1 handoff; Jay 10-13: build the EHR half).
 *
 * Two records, written only by the EHR:
 *   - the practice's tiers (Manage settings → Data & settings → Sliding scale): a name, Percent off or
 *     Dollar amount off, and an amount;
 *   - each client's sliding scale (Demographics → Insurance / Payor): a tier, or this client's own
 *     percent or dollar amount, an effective date, a review-by date, and a history of every change.
 *
 * One direction: the chart sets it, invoices only read it (`slidingFor`). The discount comes off what
 * the client owes after insurance, never off what a payor owes (`discountOn`). After Review by the
 * discount keeps applying (no bill jumps overnight) and `reviewDue` says it's due.
 */
export const SLIDING_MODES = ['pct', 'usd'];
/** 'tier' = use the tier; 'pct' / 'usd' = this client's own amount, which wins over the tier. */
export const SLIDING_OWN = ['tier', 'pct', 'usd'];
export const SLIDING_EDIT_ROLES = ['admin', 'manager'];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const money = z.coerce.number().finite();
export const slidingTierSchema = z.object({
    /** Omitted (or unknown) = a new tier. */
    id: z.string().max(64).optional(),
    name: z.string().trim().min(1, 'Give the tier a name.').max(60),
    mode: z.enum(SLIDING_MODES),
    amount: money.positive('Enter an amount more than 0.').max(100_000),
});
export const slidingTiersInputSchema = z
    .object({
    tiers: z.array(slidingTierSchema).max(50),
    /** A removed tier that clients are on: where they move (another tier's id, or null = full fee). */
    moves: z.record(z.string(), z.string().nullable()).optional(),
})
    .superRefine((v, ctx) => {
    const seen = new Set();
    v.tiers.forEach((t, i) => {
        if (t.mode === 'pct' && t.amount > 100)
            ctx.addIssue({
                code: 'custom',
                path: ['tiers', i, 'amount'],
                message: 'A percent can be 100 at most.',
            });
        const k = t.name.trim().toLowerCase();
        if (seen.has(k))
            ctx.addIssue({
                code: 'custom',
                path: ['tiers', i, 'name'],
                message: 'Another tier already has this name.',
            });
        seen.add(k);
    });
});
export const clientSlidingScaleSchema = z
    .object({
    tierId: z.string().max(64).nullable(),
    own: z.enum(SLIDING_OWN),
    ownAmount: money.nullable().optional(),
    effective: isoDate.nullable(),
    reviewBy: isoDate.nullable(),
})
    .superRefine((v, ctx) => {
    const a = v.ownAmount ?? 0;
    if (v.own === 'pct' && !(a > 0 && a <= 100))
        ctx.addIssue({
            code: 'custom',
            path: ['ownAmount'],
            message: 'Enter a percent from 1 to 100.',
        });
    if (v.own === 'usd' && !(a > 0))
        ctx.addIssue({
            code: 'custom',
            path: ['ownAmount'],
            message: 'Enter the dollar amount off, more than $0.',
        });
    const using = !!v.tierId || v.own !== 'tier';
    if (using && !v.effective)
        ctx.addIssue({
            code: 'custom',
            path: ['effective'],
            message: 'Enter the date the discount starts.',
        });
    if (using && !v.reviewBy)
        ctx.addIssue({
            code: 'custom',
            path: ['reviewBy'],
            message: 'Enter the date to review this discount.',
        });
    if (v.effective && v.reviewBy && v.reviewBy <= v.effective)
        ctx.addIssue({
            code: 'custom',
            path: ['reviewBy'],
            message: 'Review by must be after the effective date.',
        });
});
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export function slidingMoney(n) {
    return ('$' +
        r2(n)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ','));
}
/** "50% off" / "$12.00 off" */
export function discountLabel(mode, amount) {
    return mode === 'usd' ? `${slidingMoney(amount)} off` : `${Number(amount)}% off`;
}
/** "Tier 2 · 50% off", or "None (full fee)" */
export function tierLabel(t) {
    return t ? `${t.name} · ${discountLabel(t.mode, t.amount)}` : 'None (full fee)';
}
/**
 * How a client's setting reads in History and on the chart's "On invoices" line:
 * "Full fee", "Tier 2 (50% off)", "Tier 3, this client: $12.00 off", "This client: 30% off".
 */
export function describeSliding(s, tiers) {
    if (!s)
        return 'Full fee';
    const t = s.tierId ? (tiers.find((x) => x.id === s.tierId) ?? null) : null;
    if (s.own === 'pct' || s.own === 'usd') {
        const a = Number(s.ownAmount) || 0;
        if (!(a > 0))
            return t ? `${t.name} (${discountLabel(t.mode, t.amount)})` : 'Full fee';
        return (t ? `${t.name}, this client: ` : 'This client: ') + discountLabel(s.own, a);
    }
    return t ? `${t.name} (${discountLabel(t.mode, t.amount)})` : 'Full fee';
}
/**
 * THE READ FUNCTION invoices call: what applies on `dateIso` (YYYY-MM-DD), or null for the full fee
 * (before the effective date, no tier and no own amount, or a zero amount). After Review by it
 * still applies, with reviewDue.
 */
export function slidingFor(s, tiers, dateIso) {
    if (!s)
        return null;
    if (s.effective && dateIso < s.effective)
        return null;
    const t = s.tierId ? (tiers.find((x) => x.id === s.tierId) ?? null) : null;
    let mode;
    let amount;
    let source;
    if (s.own === 'pct' || s.own === 'usd') {
        mode = s.own;
        amount = Number(s.ownAmount) || 0;
        source = 'client';
    }
    else if (t) {
        mode = t.mode;
        amount = Number(t.amount) || 0;
        source = 'tier';
    }
    else
        return null;
    if (!(amount > 0))
        return null;
    const label = source === 'tier'
        ? `${t.name} · ${discountLabel(mode, amount)}`
        : t
            ? `${t.name}, this client: ${discountLabel(mode, amount)}`
            : `This client: ${discountLabel(mode, amount)}`;
    return {
        mode,
        amount,
        label,
        source,
        tierId: s.tierId ?? null,
        tierName: t ? t.name : null,
        effective: s.effective ?? null,
        reviewBy: s.reviewBy ?? null,
        reviewDue: !!(s.reviewBy && dateIso > s.reviewBy),
    };
}
/** The dollar discount on a client's share (never on what a payor owes); never more than the share. */
export function discountOn(clientShare, applies) {
    if (!applies || !(clientShare > 0))
        return 0;
    return r2(applies.mode === 'usd'
        ? Math.min(clientShare, applies.amount)
        : (clientShare * applies.amount) / 100);
}
/**
 * ehr#375 (Jay 10-30): a client's amount auto-fills from the practice tier and can be changed for
 * that client. An "own" amount that is exactly the tier's (same type, same amount) is the tier: it is
 * saved as `own: 'tier'`, so a later change to the tier in Data & settings reaches this client too.
 * Any other amount is this client's own, and that is what invoices use.
 */
export function normalizeClientSliding(s, tiers) {
    if (s.own !== 'pct' && s.own !== 'usd')
        return s;
    const t = s.tierId ? tiers.find((x) => x.id === s.tierId) : undefined;
    if (!t || t.mode !== s.own || s.ownAmount == null)
        return s;
    if (r2(Number(s.ownAmount)) !== r2(Number(t.amount)))
        return s;
    return { ...s, own: 'tier', ownAmount: null };
}
/** YYYY-MM-DD one year after an ISO date (Feb 29 → Mar 1). */
export function addYearIso(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y + 1, m - 1, d)).toISOString().slice(0, 10);
}
//# sourceMappingURL=slidingScale.js.map